import { useState } from "react";
import { motion } from "framer-motion";
import { CarFront, Footprints, MapPin, Flag, Home } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const heureFr = (h) => (h ? h.replace(":", "h") : "");

export const covoitMessage = (lieu, heure) => {
  if (!lieu && !heure) return "Le lieu et l'heure du rendez-vous au club seront précisés par le coach.";
  return `Je serai présent${lieu ? ` à ${lieu}` : ""}${heure ? ` à ${heureFr(heure)}` : ""}.`;
};

export const placesTxt = (n) => (n === 0 ? "Voiture pleine, aucune place libre" : `${n} place${n > 1 ? "s" : ""} disponible${n > 1 ? "s" : ""}`);
export const directTxt = (type) => (type === "tournoi" ? "Directement au tournoi" : "Directement au match");
const CLUB = ["club", "sans_voiture", "places"];

const Choice = ({ id, active, onClick, Icon, label }) => (
  <motion.button type="button" whileTap={{ scale: 0.96 }} data-testid={`convocation-form-transport-${id}-button`} aria-pressed={active} onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-2xl border px-3 py-3 text-sm font-semibold leading-tight transition-colors duration-300 ${active ? "border-lime bg-lime/15 text-lime" : "border-line bg-ink text-chalk hover:border-chalk/50"}`}>
    <Icon className="h-5 w-5 shrink-0" />{label}
  </motion.button>
);

const PlacesDialog = ({ open, onOpenChange, places, onPick }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="places-dialog" className="w-[calc(100%-2rem)] max-w-md rounded-[24px] border-lime/60 bg-pitch text-chalk">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 font-display text-2xl uppercase text-lime"><CarFront className="h-6 w-6" />Places disponibles</DialogTitle>
        <DialogDescription className="text-base text-chalk">Combien de places sont disponibles dans ton véhicule pour d'autres enfants ?</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-4 gap-2">
        {[0, 1, 2, 3, 4, 5, 6].map((n) => (
          <button key={n} type="button" data-testid={`convocation-form-places-${n}-button`} aria-pressed={places === n} onClick={() => onPick(n)}
            className={`rounded-xl border py-3 font-display text-2xl transition-colors duration-300 ${places === n ? "border-lime bg-lime text-ink" : "border-line bg-ink text-chalk hover:border-lime"}`}>{n}</button>
        ))}
      </div>
      <p className="text-xs text-dim">0 = voiture pleine. Si le nombre change, reviens sur cette page et renvoie ta réponse.</p>
    </DialogContent>
  </Dialog>
);

const Label = ({ children }) => <span className="mb-1 block font-mono text-xs uppercase tracking-[0.2em] text-dim sm:mb-2">{children} <span className="text-lime">*</span></span>;

export const TransportChoice = ({ type, transport, places, onTransport, onPlaces, lieu, heure, error }) => {
  const [open, setOpen] = useState(false);
  const club = CLUB.includes(transport);
  const choisirVoiture = () => { onTransport("places"); setOpen(true); };
  return (
    <div data-testid="convocation-form-transport" className="block">
      <Label>Comment viens-tu ?</Label>
      <div className="grid grid-cols-2 gap-3">
        <Choice id="direct" active={transport === "direct"} onClick={() => onTransport("direct")} Icon={Flag} label={directTxt(type)} />
        <Choice id="club" active={club} onClick={() => { if (!club) onTransport("club"); }} Icon={Home} label="Rendez-vous au club" />
      </div>
      {club && (
        <div data-testid="convocation-form-club-block" className="mt-3 space-y-3">
          <p data-testid="convocation-form-covoit-message" className="flex items-start gap-2 rounded-2xl border border-lime/40 bg-ink p-3 text-sm text-chalk">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-lime" />{covoitMessage(lieu, heure)}
          </p>
          <div>
            <Label>As-tu de la place dans ton véhicule ?</Label>
            <div className="grid grid-cols-2 gap-3">
              <Choice id="sans-voiture" active={transport === "sans_voiture"} onClick={() => onTransport("sans_voiture")} Icon={Footprints} label="Pas de voiture" />
              <Choice id="places" active={transport === "places"} onClick={choisirVoiture} Icon={CarFront} label="J'ai une voiture" />
            </div>
          </div>
          {transport === "places" && (
            <div data-testid="convocation-form-places-summary" className="flex items-center justify-between gap-3 rounded-2xl border border-lime/40 bg-ink p-3 text-sm text-chalk">
              <span className="flex items-center gap-2"><CarFront className="h-4 w-4 shrink-0 text-lime" />{places === null || places === undefined ? "Nombre de places à indiquer" : placesTxt(places)}</span>
              <button type="button" data-testid="convocation-form-places-edit-button" onClick={() => setOpen(true)} className="shrink-0 text-lime underline-offset-4 hover:underline">
                {places === null || places === undefined ? "Indiquer" : "Modifier"}
              </button>
            </div>
          )}
        </div>
      )}
      {error && <span data-testid="convocation-form-transport-error" className="mt-2 block text-sm text-signal">{error}</span>}
      <PlacesDialog open={open} onOpenChange={setOpen} places={places} onPick={(n) => { onPlaces(n); setOpen(false); }} />
    </div>
  );
};
